import { useState, useEffect, type FormEvent, type ReactNode } from "react";
import { authClient } from "./client";
import { friendlyAuthError } from "../ui/authErrors";
import { showToast } from "../ui/Toast";
import { authStyles } from "./authStyles";

export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

interface AuthGateProps {
  children: (session: { user: { id: string; name: string; email: string } }) => ReactNode;
}

export default function AuthGate({ children }: AuthGateProps) {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    authClient.getSession().then(({ data, error: err }) => {
      if (data) setSession(data);
      if (err) console.warn("Session check failed:", err);
      setLoading(false);
    });
    if (new URLSearchParams(window.location.search).get("reset") === "success") {
      showToast("Password updated. Sign in with your new password.", "success");
      history.replaceState({}, "", window.location.pathname + window.location.hash);
    }
  }, []);

  if (loading) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.card}>
          <div style={styles.loadingText}>Loading...</div>
        </div>
      </div>
    );
  }

  if (session) {
    return <>{children(session)}</>;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    const normalizedEmail = normalizeEmail(email);
    const cleanedPassword = password.trim();

    if (mode === "register" && cleanedPassword !== confirmPassword.trim()) {
      setError("Passwords don't match.");
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "register") {
        const { data, error: err } = await authClient.signUp.email({
          email: normalizedEmail,
          password: cleanedPassword,
          name: name.trim() || normalizedEmail.split("@")[0],
        });
        if (err) {
          setError(friendlyAuthError(err, "Registration failed"));
        } else if (data) {
          const { data: sess } = await authClient.getSession();
          if (sess) setSession(sess);
          else setError("Registered but failed to get session. Please log in.");
        }
      } else {
        const { data, error: err } = await authClient.signIn.email({
          email: normalizedEmail,
          password: cleanedPassword,
        });
        if (err) {
          setError(friendlyAuthError(err, "Login failed"));
        } else if (data) {
          const { data: sess } = await authClient.getSession();
          if (sess) setSession(sess);
        }
      }
    } catch (ex: unknown) {
      setError(friendlyAuthError(ex, "Something went wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    authClient.signIn.social({ provider: "google" });
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <div style={styles.logo}>
            <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" width="14" height="14">
              <path d="M2 9L6 2L10 9H2Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={styles.brand}>CAD-PLM</span>
        </div>

        <h2 style={styles.heading}>
          {mode === "login" ? "Sign in" : "Create account"}
        </h2>

        <form onSubmit={handleSubmit} style={styles.form}>
          {mode === "register" && (
            <input
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={styles.input}
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={styles.input}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            style={styles.input}
          />
          {mode === "register" && (
            <input
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              style={styles.input}
              data-testid="confirm-password"
            />
          )}

          {mode === "login" && (
            <a href="/auth/reset" style={styles.forgotLink} data-testid="forgot-password">
              Forgot password?
            </a>
          )}

          {error && <div style={styles.error}>{error}</div>}

          <button type="submit" disabled={submitting} style={styles.primaryBtn}>
            {submitting ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div style={styles.divider}>
          <span style={styles.dividerLine} />
          <span style={styles.dividerText}>or</span>
          <span style={styles.dividerLine} />
        </div>

        <button onClick={handleGoogle} style={styles.googleBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        <p style={styles.toggle}>
          {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
          <button
            type="button"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setError("");
              setConfirmPassword("");
            }}
            style={styles.toggleBtn}
          >
            {mode === "login" ? "Sign up" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  ...authStyles,
  divider: { display: "flex", alignItems: "center", gap: 12, margin: "18px 0" },
  dividerLine: { flex: 1, height: 1, background: "var(--border-default)" },
  dividerText: {
    fontSize: 11,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  googleBtn: {
    width: "100%",
    height: 38,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    background: "var(--bg-surface)",
    color: "var(--text-primary)",
    border: "1px solid var(--border-default)",
    borderRadius: "var(--radius-md)",
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "var(--sans)",
    cursor: "pointer",
  },
  toggle: { textAlign: "center", fontSize: 12, color: "var(--text-muted)", marginTop: 18 },
  loadingText: { color: "var(--text-muted)", fontSize: 13, textAlign: "center" },
};
